import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { isAuthRequired } from '@/lib/environment';
import { FamilyMember } from '@/types/familyMember';
import { FamilyMemberEmail, FAMILY_MEMBER_EMAIL_COLUMNS, dbRowToFamilyMemberEmail } from '@/types/familyMemberEmail';
import { useNicknames } from './useNicknames';
import { toast } from '@/lib/toast';
import { FAMILY_MEMBER_COLUMNS, dbRowToMember, mapMemberToDb, deserializeMemberFromCache } from '@/logic/familyMapper';

const FAMILY_CACHE_PREFIX = 'team-v-does-family-cache:';
const FAMILY_DEV_CACHE_PREFIX = 'team-v-does-family-cache-dev:';
const DEV_IMPERSONATE_MEMBER_KEY = 'team-v-does-dev-impersonate-member-id';

const getFamilyCacheKey = (teamId: string) => `${FAMILY_CACHE_PREFIX}${teamId}`;
const getFamilyDevCacheKey = (teamId: string) => `${FAMILY_DEV_CACHE_PREFIX}${teamId}`;

const loadFamilyFromCache = (teamId: string): FamilyMember[] => {
  if (isAuthRequired()) {
    try {
      const lastUserId = localStorage.getItem('team-v-does-last-user-id');
      if (!lastUserId) return [];
    } catch {
      return [];
    }
  }
  try {
    const raw = localStorage.getItem(getFamilyCacheKey(teamId));
    if (!raw && !isAuthRequired()) {
      const devRaw = localStorage.getItem(getFamilyDevCacheKey(teamId));
      if (!devRaw) return [];
      return JSON.parse(devRaw).map((row: any) => deserializeMemberFromCache(row));
    }
    if (!raw) return [];
    return JSON.parse(raw).map((row: any) => deserializeMemberFromCache(row));
  } catch {
    return [];
  }
};

const saveFamilyToCache = (teamId: string, members: FamilyMember[]) => {
  try {
    const payload = members.map((m) => ({
      ...m,
      createdAt: m.createdAt.toISOString(),
      lastActiveDate: m.lastActiveDate ? m.lastActiveDate.toISOString() : null,
    }));
    localStorage.setItem(getFamilyCacheKey(teamId), JSON.stringify(payload));
    if (!isAuthRequired()) {
      localStorage.setItem(getFamilyDevCacheKey(teamId), JSON.stringify(payload));
    }
  } catch {
    // ignore
  }
};

export function useFamilyMembers(teamId: string | null, authUserId?: string | null, authUserEmail?: string | null) {
  const [members, setMembers] = useState<FamilyMember[]>(() => (teamId ? loadFamilyFromCache(teamId) : []));
  const [loading, setLoading] = useState(() => (teamId ? loadFamilyFromCache(teamId).length === 0 : true));
  const [memberAvatars, setMemberAvatars] = useState<Record<string, string | null>>({});
  const [devImpersonateId, setDevImpersonateId] = useState<string | null>(() => {
    if (isAuthRequired()) return null;
    try {
      return localStorage.getItem(DEV_IMPERSONATE_MEMBER_KEY);
    } catch {
      return null;
    }
  });

  const fetchMembers = useCallback(async () => {
    if (!teamId) {
      setMembers([]);
      setLoading(false);
      return;
    }

    // Fetch family members and their emails in parallel
    const [membersResult, emailsResult] = await Promise.all([
      supabase
        .from('family_members')
        .select(FAMILY_MEMBER_COLUMNS)
        .eq('team_id', teamId)
        .eq('is_visible', true)
        .order('sort_order'),
      supabase
        .from('family_member_emails')
        .select(FAMILY_MEMBER_EMAIL_COLUMNS)
        .eq('team_id', teamId)
        .order('is_primary', { ascending: false })
        .order('created_at'),
    ]);

    if (membersResult.error) {
      console.error('Error fetching members:', membersResult.error);
      if (!isAuthRequired()) {
        const cached = loadFamilyFromCache(teamId);
        if (cached.length > 0) {
          setMembers(cached);
        }
      }
      setLoading(false);
      return;
    }

    // Parse emails
    const emails: FamilyMemberEmail[] = emailsResult.data?.map(dbRowToFamilyMemberEmail) || [];

    // Build members with their emails
    const nextMembers = membersResult.data?.map(row => dbRowToMember(row, emails)) || [];
    setMembers(nextMembers);
    saveFamilyToCache(teamId, nextMembers);
    setLoading(false);

    // Fetch avatars for all members that have a linked userId
    const userIds = nextMembers
      .map(m => m.userId)
      .filter((id): id is string => Boolean(id));
    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, avatar_url')
        .in('id', userIds);
      if (profiles) {
        const avatarMap: Record<string, string | null> = {};
        // map by userId → avatar_url
        profiles.forEach(p => { avatarMap[p.id] = p.avatar_url ?? null; });
        // now convert to memberId → avatar_url
        const memberAvatarMap: Record<string, string | null> = {};
        nextMembers.forEach(m => {
          if (m.userId && avatarMap[m.userId] !== undefined) {
            memberAvatarMap[m.id] = avatarMap[m.userId];
          }
        });
        setMemberAvatars(memberAvatarMap);
      }
    }
  }, [teamId]);

  const lastFetchRef = useRef<number>(0);

  useEffect(() => {
    if (teamId) {
      const cached = loadFamilyFromCache(teamId);
      if (cached.length > 0) {
        setMembers(cached);
        setLoading(false);
      }
    }
    fetchMembers().then(() => { lastFetchRef.current = Date.now(); });

    if (typeof document === 'undefined') return;
    // Realtime removed — family members change very rarely.
    // Throttled refetch on tab focus (10 min min interval).
    const FAMILY_REFETCH_MIN_INTERVAL_MS = 10 * 60 * 1000;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFetchRef.current < FAMILY_REFETCH_MIN_INTERVAL_MS) return;
      fetchMembers().then(() => { lastFetchRef.current = Date.now(); });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchMembers, teamId]);

  // Preserve last-known currentUser across transient auth loading gaps.
  // When the app resumes from background (or process kill), authUserId may
  // briefly be undefined while the session restores. Without this ref,
  // currentUserId flashes to null, causing FYPView to show "Log in" fallback.
  const lastKnownCurrentUserRef = useRef<FamilyMember | null>(null);

  const currentUser = (() => {
    if (authUserId) {
      const matched = members.find((m) =>
        m.userId === authUserId ||
        m.emails.some(e => e.userId === authUserId)
      );
      if (matched) return matched;

      if (authUserEmail) {
        const normalizedEmail = authUserEmail.toLowerCase();
        const emailMatched = members.find((m) =>
          m.email?.toLowerCase() === normalizedEmail ||
          m.emails.some(e => e.email.toLowerCase() === normalizedEmail)
        );
        if (emailMatched) return emailMatched;
      }
    }

    if (!isAuthRequired() && devImpersonateId) {
      return members.find((m) => m.id === devImpersonateId) || null;
    }

    // Auth is still loading (authUserId is undefined, not null) — keep last known user
    // to prevent UI flash. Only return null when auth has explicitly resolved with no user.
    if (authUserId === undefined && lastKnownCurrentUserRef.current) {
      return lastKnownCurrentUserRef.current;
    }

    return null;
  })();

  // Update the ref whenever we resolve a real user
  if (currentUser) {
    lastKnownCurrentUserRef.current = currentUser;
  } else if (authUserId === null) {
    // Explicitly no auth user (signed out) — clear the ref
    lastKnownCurrentUserRef.current = null;
  }

  const currentUserId = currentUser?.id || null;

  // Link a family member to a user_id
  const linkMemberToUser = useCallback(async (memberId: string, userId: string) => {
    const { error } = await supabase
      .from('family_members')
      .update(mapMemberToDb({ userId }))
      .eq('id', memberId);

    if (error) {
      console.error('Error linking member to user:', error);
      return false;
    }

    setMembers(prev =>
      prev.map(m => m.id === memberId ? { ...m, userId } : m)
    );
    return true;
  }, []);

  // setCurrentUserId: dev impersonation, auth-driven in prod (but allows initial "claim")
  const setCurrentUserId = useCallback((_id: string | null) => {
    if (isAuthRequired()) {
      if (authUserId && !currentUserId && _id) {
        toast.promise(linkMemberToUser(_id, authUserId), {
          loading: 'Identiteit koppelen...',
          success: 'Je bent nu gekoppeld!',
          error: 'Koppelen mislukt. Probeer later opnieuw.'
        }, { channel: 'teams' });
      } else {
        console.warn('setCurrentUserId ignored - user already linked or no ID provided');
      }
      return;
    }
    const nextId = _id || null;
    setDevImpersonateId(nextId);
    try {
      if (nextId) {
        localStorage.setItem(DEV_IMPERSONATE_MEMBER_KEY, nextId);
      } else {
        localStorage.removeItem(DEV_IMPERSONATE_MEMBER_KEY);
      }
    } catch {
      // ignore
    }
  }, [authUserId, currentUserId, linkMemberToUser]);

  const addMember = useCallback(async (name: string): Promise<string | null> => {
    if (!teamId) {
      toast.error('Selecteer eerst een team', { channel: 'teams' });
      return null;
    }

    const trimmedName = name.trim();
    if (!trimmedName) return null;

    const { data, error } = await supabase
      .from('family_members')
      .insert(mapMemberToDb({ name: trimmedName, teamId }))
      .select('id')
      .single();

    if (error) {
      if (error.code === '23505') {
        toast.error('Deze naam bestaat al', { channel: 'teams' });
      } else {
        console.error('Error adding member:', error);
        toast.error('Fout bij toevoegen van naam', { channel: 'teams' });
      }
      return null;
    }

    toast.success(`${trimmedName} toegevoegd`, { channel: 'teams' });
    return data.id;
  }, [teamId]);

  const updateMember = useCallback(async (id: string, updates: { name?: string; email?: string | null }) => {
    const dbUpdates = mapMemberToDb(updates);

    if (Object.keys(dbUpdates).length === 0) return;

    const { error } = await supabase
      .from('family_members')
      .update(dbUpdates)
      .eq('id', id);

    if (error) {
      console.error('Error updating member:', error);
      if (error.code === '23505') {
        toast.error('Dit e-mailadres is al in gebruik', { channel: 'teams' });
      } else {
        toast.error('Fout bij het bijwerken van lid', { channel: 'teams' });
      }
      return;
    }

    setMembers(prev =>
      prev.map(m => m.id === id ? { ...m, ...updates } : m)
    );
  }, []);


  // Find member by email
  const findMemberByEmail = useCallback((email: string): FamilyMember | undefined => {
    const normalizedEmail = email.toLowerCase();
    return members.find(m =>
      m.email?.toLowerCase() === normalizedEmail ||
      m.emails.some(e => e.email.toLowerCase() === normalizedEmail)
    );
  }, [members]);

  // Find member by user_id
  const findMemberByUserId = useCallback((userId: string): FamilyMember | undefined => {
    return members.find(m =>
      m.userId === userId ||
      m.emails.some(e => e.userId === userId)
    );
  }, [members]);

  const reorderMembers = useCallback(async (reorderedMembers: FamilyMember[]) => {
    setMembers(reorderedMembers);

    const updates = reorderedMembers.map((member, index) =>
      supabase
        .from('family_members')
        .update(mapMemberToDb({ sortOrder: index }))
        .eq('id', member.id)
    );

    const results = await Promise.all(updates);
    const hasError = results.some(r => r.error);

    if (hasError) {
      console.error('Error reordering members');
      toast.error('Fout bij het herordenen van leden', { channel: 'teams' });
      fetchMembers();
    }
  }, [fetchMembers]);

  const deleteMember = useCallback(async (id: string) => {
    const { error } = await supabase
      .from('family_members')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting member:', error);
      toast.error('Fout bij het verwijderen van lid', { channel: 'teams' });
    }
  }, []);

  const { nicknames } = useNicknames(teamId);

  const getDisplayName = useCallback((targetMemberId: string, viewerMemberId?: string): string => {
    const target = members.find(m => m.id === targetMemberId);
    if (!target) return 'Onbekend';

    const viewer = viewerMemberId || currentUserId;
    if (!viewer) return target.name;

    const nicknameObj = nicknames.find(
      n => n.viewerId === viewer && n.targetId === targetMemberId
    );

    return nicknameObj ? nicknameObj.nickname : target.name;
  }, [members, currentUserId, nicknames]);

  const getMemberAvatarUrl = useCallback((memberId: string): string | null => {
    return memberAvatars[memberId] ?? null;
  }, [memberAvatars]);

  return {
    members,
    currentUser,
    currentUserId,
    setCurrentUserId,
    addMember,
    updateMember,
    deleteMember,
    reorderMembers,
    linkMemberToUser,
    findMemberByEmail,
    findMemberByUserId,
    getDisplayName,
    getMemberAvatarUrl,
    loading,
  };
}
